import type {
  RunAggregateProjectionV2,
  RunDefinitionProjection,
  WebTestRun
} from '@blume/web-test-model'
import { ChevronRight } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { createDashboardApi } from '@/api'
import { relativeTime } from '@/design/format'
import { RunStatusBadge } from '@/design/status'
import { titleCase } from '@/logic'
import { scenarioEmoji } from '@/ui/atoms/avatar-tile'

import { scenarioStatus, scenarioSummaries, suiteHref } from './entity-shared'
import { BackLink, GroupCard, MetaLine, RunList, SectionLabel } from './parts'
import { ScenarioEmojiPicker } from './scenario-emoji-picker'

function useScenarioDefinition(runId: string | undefined): {
  definition?: RunDefinitionProjection
  unavailable: boolean
} {
  const [definition, setDefinition] = useState<RunDefinitionProjection>()
  const [unavailable, setUnavailable] = useState(false)
  const apiBaseUrl = import.meta.env.VITE_WEB_TEST_API_BASE_URL
  useEffect(() => {
    if (!runId || !apiBaseUrl) {
      setDefinition(undefined)
      setUnavailable(Boolean(runId))
      return
    }
    let cancelled = false
    setDefinition(undefined)
    setUnavailable(false)
    void createDashboardApi(apiBaseUrl)
      .getRunDefinition(runId)
      .then((next) => {
        if (!cancelled) setDefinition(next)
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true)
      })
    return () => {
      cancelled = true
    }
  }, [apiBaseUrl, runId])
  return { definition, unavailable }
}

function ScenarioDefinition({
  definition,
  unavailable
}: {
  definition?: RunDefinitionProjection
  unavailable: boolean
}) {
  if (!definition) {
    return (
      <section aria-label="Scenario definition" className="mb-10 max-w-3xl">
        <SectionLabel>Scenario definition</SectionLabel>
        <p className="mt-2 text-sm text-muted-foreground">
          {unavailable
            ? 'The exact persisted definition is unavailable for this retained run.'
            : 'Loading the exact persisted definition…'}
        </p>
      </section>
    )
  }
  const scenario = definition.scenario
  return (
    <section aria-label="Scenario definition" className="mb-10 max-w-3xl">
      <SectionLabel>Scenario definition</SectionLabel>
      <h2 className="mt-2 text-lg font-semibold tracking-tight">{scenario.title}</h2>
      <p className="mt-1 text-sm leading-6 text-muted-foreground">{scenario.intent}</p>
      <ol className="mt-4 space-y-2">
        {scenario.steps.map((step) => (
          <li key={step.id} className="text-sm">
            <span className="font-medium">{step.description}</span>
            <span className="ml-2 font-mono text-[12px] text-muted-foreground">{step.id}</span>
            {step.explorationHint ? (
              <p className="mt-0.5 text-[13px] text-muted-foreground">{step.explorationHint}</p>
            ) : null}
          </li>
        ))}
      </ol>
      <details className="mt-5 rounded-md border border-border px-3 py-2">
        <summary className="cursor-pointer text-sm font-medium">
          Committed executable action plan
        </summary>
        {definition.plan ? (
          <pre className="mt-3 overflow-x-auto text-xs leading-5 text-muted-foreground">
            {JSON.stringify(definition.plan, null, 2)}
          </pre>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            No committed plan existed for this first-run exploration.
          </p>
        )}
      </details>
      <details className="mt-3 rounded-md border border-border px-3 py-2">
        <summary className="cursor-pointer text-sm font-medium">AI exploration context</summary>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {definition.exploration.contextSummary}
        </p>
        {definition.exploration.systemInstruction ? (
          <pre className="mt-3 overflow-x-auto whitespace-pre-wrap text-xs leading-5 text-muted-foreground">
            {definition.exploration.systemInstruction}
          </pre>
        ) : null}
      </details>
    </section>
  )
}

/**
 * One scenario as an entity: what it is, its latest and aggregate state,
 * where it has been observed running (many-to-many suite-run contexts plus
 * ad-hoc execution), and the loaded run history — with what remains
 * unloaded or unknown stated honestly rather than implied away.
 */
export function ScenarioDetailPage({
  runs,
  aggregates,
  scenarioId,
  now
}: {
  runs: WebTestRun[]
  aggregates?: RunAggregateProjectionV2[]
  scenarioId: string
  now: number
}) {
  const scenarios = useMemo(() => scenarioSummaries(runs, aggregates), [aggregates, runs])
  const scenario = scenarios.find((candidate) => candidate.scenarioId === scenarioId)
  const definition = useScenarioDefinition(scenario?.latest?.id ?? scenario?.loadedRuns[0]?.id)

  if (!scenario) {
    return (
      <>
        <BackLink href="#/scenarios" label="Scenarios" />
        <header className="max-w-3xl">
          <h1 className="font-mono text-[28px] font-semibold tracking-tight break-words">
            {scenarioId}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            No runs are recorded for this scenario. They may have been retained out, or the link is
            stale.
          </p>
        </header>
      </>
    )
  }

  const status = scenarioStatus(scenario)
  const progress = scenario.progress
  const loaded = [...scenario.loadedRuns].sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  )
  const unloaded = Math.max(0, scenario.runCount - loaded.length)
  const decisiveOutsidePage =
    progress.primaryRunId !== undefined && !loaded.some((run) => run.id === progress.primaryRunId)
      ? progress.primaryRunId
      : undefined

  return (
    <>
      <BackLink href="#/scenarios" label="Scenarios" />

      <header className="mb-8 max-w-3xl">
        <div className="flex items-start gap-3">
          <ScenarioEmojiPicker
            key={`${scenario.scenarioId}:${definition.definition?.scenario.emoji ?? ''}:${scenario.latest?.scenarioHash ?? scenario.loadedRuns[0]?.scenarioHash ?? ''}`}
            scenarioId={scenario.scenarioId}
            initialEmoji={
              definition.definition?.scenario.emoji ?? scenarioEmoji(scenario.scenarioId)
            }
            expectedScenarioHash={
              scenario.latest?.scenarioHash ?? scenario.loadedRuns[0]?.scenarioHash
            }
          />
          <h1 className="font-mono text-[28px] font-semibold tracking-tight break-words">
            {scenario.scenarioId}
          </h1>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <RunStatusBadge meta={status.meta} active={status.active} />
          <MetaLine
            items={[
              scenario.targets.length > 0 ? scenario.targets.map(titleCase).join(', ') : undefined,
              `${scenario.runCount} run${scenario.runCount === 1 ? '' : 's'}`,
              `last run ${relativeTime(scenario.updatedAt, now)}`
            ]}
          />
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{progress.summary}</p>
        <p className="mt-4 max-w-prose text-[13px] text-muted-foreground">
          One reusable test definition. It can belong to several suites at once and can also run
          ad-hoc outside any suite;{' '}
          {scenario.latest
            ? 'the status above reflects its latest loaded run.'
            : 'the latest run is not loaded, so the status above summarizes the durable aggregate.'}
        </p>
      </header>

      <ScenarioDefinition {...definition} />

      <section aria-label="Where this scenario runs" className="mb-10 max-w-3xl">
        <SectionLabel>Observed suite runs</SectionLabel>
        {scenario.loadedCount === 0 ? (
          <p className="mt-2 text-[13px] text-muted-foreground">
            Membership unknown — none of the {scenario.runCount} run
            {scenario.runCount === 1 ? ' is' : 's are'} loaded on this page.
          </p>
        ) : (
          <>
            {scenario.suiteRunIds.length > 0 ? (
              <ul className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1.5">
                {scenario.suiteRunIds.map((suiteRunId) => (
                  <li key={suiteRunId}>
                    <Badge asChild variant="secondary" className="font-mono font-normal">
                      <a
                        href={suiteHref(suiteRunId)}
                        className="focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
                      >
                        {suiteRunId}
                        <ChevronRight aria-hidden className="size-3 shrink-0" />
                        <span className="sr-only"> — open suite page</span>
                      </a>
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-[13px] text-muted-foreground">
                No suite membership observed among the loaded runs.
              </p>
            )}
            {scenario.hasAdHoc ? (
              <p className="mt-2 text-[13px] text-muted-foreground">
                Ad-hoc execution observed — {scenario.suiteRunIds.length > 0 ? 'some' : 'the'}{' '}
                loaded run{scenario.loadedCount === 1 ? '' : 's'} started outside any suite.
              </p>
            ) : null}
            <p className="mt-2 max-w-prose text-[13px] text-muted-foreground">
              Observed from the {scenario.loadedCount} loaded run
              {scenario.loadedCount === 1 ? '' : 's'}
              {unloaded > 0 ? ` of ${scenario.runCount} total` : ''}. These are suite-run execution
              contexts, not the authored suite definition — this scenario may additionally belong to
              suites whose runs are not loaded here.
            </p>
          </>
        )}
      </section>

      <section aria-label="Run history">
        <GroupCard
          header={
            <SectionLabel>
              Loaded run history ({loaded.length} of {scenario.runCount})
            </SectionLabel>
          }
        >
          {loaded.length > 0 ? (
            <RunList runs={loaded} now={now} />
          ) : decisiveOutsidePage ? (
            <p className="py-2 text-[13px] text-muted-foreground">
              Run details are outside the loaded page.{' '}
              <a
                href={`#/runs/${encodeURIComponent(decisiveOutsidePage)}`}
                className="relative font-medium text-foreground underline underline-offset-4 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
              >
                Open the decisive run
              </a>
            </p>
          ) : (
            <p className="py-2 text-[13px] text-muted-foreground">
              None of this scenario&rsquo;s runs are loaded on this page.
            </p>
          )}
        </GroupCard>
        {loaded.length > 0 && decisiveOutsidePage ? (
          <p className="mt-2 max-w-3xl text-[13px] text-muted-foreground">
            The decisive run for this scenario is outside the loaded page.{' '}
            <a
              href={`#/runs/${encodeURIComponent(decisiveOutsidePage)}`}
              className="font-medium text-foreground underline underline-offset-4 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
            >
              Open the decisive run
            </a>
          </p>
        ) : null}
        {unloaded > 0 && loaded.length > 0 ? (
          <p className="mt-2 max-w-3xl text-[13px] text-muted-foreground">
            +{unloaded} earlier run{unloaded === 1 ? '' : 's'} {unloaded === 1 ? 'is' : 'are'} not
            loaded on this page.
          </p>
        ) : null}
      </section>
    </>
  )
}
